import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Stack,
  Card,
  CardContent,
  Divider,
  Button,
} from "@mui/material";
import {
  MoreVert as MoreVertIcon,
  Videocam as VideocamIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from "@mui/icons-material";
function getAlertRuleFavoriteKey(rule) {
  if (!rule || typeof rule !== "object") return "";
  if (rule.apiId != null && String(rule.apiId).trim() !== "") {
    return String(rule.apiId);
  }
  if (rule.id != null && String(rule.id).trim() !== "") {
    return String(rule.id);
  }
  return "";
}

function ruleHasSubSourceRefsPopupData(rule) {
  return (
    (Array.isArray(rule?.sourceRefsWithZones) &&
      rule.sourceRefsWithZones.length > 0) ||
    (Array.isArray(rule?.sourceRefsList) && rule.sourceRefsList.length > 0) ||
    (Array.isArray(rule?.zoneRefsList) && rule.zoneRefsList.length > 0)
  );
}

function ruleCanInteractSubSourceColumn(rule) {
  return (
    ruleHasSubSourceRefsPopupData(rule) ||
    Boolean(String(rule?.apiId ?? rule?.id ?? "").trim())
  );
}

function getAlertTypeColor(alertType) {
  const colorMap = {
    "Object Detection": { bg: "#E3F2FD", text: "#1976D2" },
    "Line Crossing": { bg: "#FFF3E0", text: "#F57C00" },
    "Intrusion Detection": { bg: "#FFEBEE", text: "#D32F2F" },
    "Loitering Detection": { bg: "#F3E5F5", text: "#7B1FA2" },
    "Motion Detection": { bg: "#E8F5E9", text: "#388E3C" },
    "Face Recognition": { bg: "#E1F5FE", text: "#0277BD" },
    "License Plate Recognition": { bg: "#FFF9C4", text: "#F9A825" },
    "Crowd Detection": { bg: "#FCE4EC", text: "#C2185B" },
    "Fire Detection": { bg: "#FFCCBC", text: "#E64A19" },
    "Smoke Detection": { bg: "#D7CCC8", text: "#5D4037" },
  };
  return colorMap[alertType] || { bg: "#ECEFF1", text: "#455A64" };
}

function actionChipSx(type) {
  const t = String(type).toUpperCase();
  if (t === "SMS") {
    return { backgroundColor: "#FFEBEE", color: "#C62828", fontWeight: 600 };
  }
  if (t === "EMAIL" || t === "TELEGRAM") {
    return { backgroundColor: "#E8F5E9", color: "#2E7D32", fontWeight: 600 };
  }
  return { backgroundColor: "#E3F2FD", color: "#2932E5", fontWeight: 600 };
}

function isRecentlyModified(createdDate) {
  if (!createdDate) return false;
  const created = new Date(createdDate);
  if (Number.isNaN(created.getTime())) return false;
  const diffDays = Math.ceil(
    Math.abs(Date.now() - created.getTime()) / (1000 * 60 * 60 * 24),
  );
  return diffDays <= 7;
}

export function AlertRuleGridView({
  rules,
  onMenuOpen,
  onContextMenu,
  favoriteKeys = [],
  onToggleFavorite,
  /** (event, rule) — open view rule + Sub Source browse (same as table Sub-source link). */
  onSubSourceClick,
}) {
  const [hoveredCard, setHoveredCard] = useState(null);
  const favoriteKeySet = useMemo(() => new Set(favoriteKeys), [favoriteKeys]);

  return (
    <Box
      sx={{
        height: "calc(100vh - 325px)",
        overflow: "auto",
        p: { xs: 1, sm: 1.5 },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 2,
          alignItems: "stretch",
        }}
      >
        {rules.map((rule) => {
          const alertTypeColor = getAlertTypeColor(rule.alertType);
          const showRecentBadge = isRecentlyModified(rule.createdDate);
          const actions = Array.isArray(rule.actionTypes) ? rule.actionTypes : [];
          const ruleName = rule.name || "Unnamed rule";

          return (
            <Card
              key={rule.id}
              elevation={0}
              onMouseEnter={() => setHoveredCard(rule.id)}
              onMouseLeave={() => setHoveredCard(null)}
              onContextMenu={(e) => onContextMenu?.(e, rule)}
              sx={{
                display: "flex",
                flexDirection: "column",
                minHeight: { xs: 200, sm: 228 },
                height: "100%",
                borderRadius: "14px",
                border: "1px solid rgb(229, 231, 235)",
                bgcolor: "#fff",
                boxShadow:
                  "0px 1px 2px -1px rgba(0,0,0,0.08), 0px 1px 3px 0px rgba(0,0,0,0.06)",
                overflow: "hidden",
                transition: "0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                cursor: "default",
                position: "relative",
                "&:hover": {
                  boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                  transform: "translateY(-3px)",
                  borderColor: "rgba(41, 50, 229, 0.25)",
                },
              }}
            >
              {showRecentBadge && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    zIndex: 1,
                    px: 1,
                    py: 0.35,
                    borderRadius: "20px",
                    bgcolor: "rgba(255, 133, 85, 0.95)",
                    color: "#fff",
                    fontSize: "10px",
                    fontWeight: 700,
                    letterSpacing: "0.04em",
                    fontFamily: "Roboto, sans-serif",
                  }}
                >
                  NEW
                </Box>
              )}

              <CardContent
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  p: 1.75,
                  pt: showRecentBadge ? 3.25 : 1.75,
                  "&:last-child": { pb: 1.75 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 1.25,
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Tooltip title={ruleName} arrow placement="top">
                      <Typography
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontWeight: 700,
                          fontSize: "16px",
                          color: "#212121",
                          lineHeight: 1.35,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {ruleName}
                      </Typography>
                    </Tooltip>
                    {rule.srId ? (
                      <Typography
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "12px",
                          color: "rgba(0,0,0,0.55)",
                          mt: 0.5,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        id: {rule.srId}
                      </Typography>
                    ) : null}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                    {onToggleFavorite && (
                      <IconButton
                        size="small"
                        aria-label={
                          favoriteKeySet.has(getAlertRuleFavoriteKey(rule))
                            ? "Remove from favorites"
                            : "Add to favorites"
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          onToggleFavorite(rule, e);
                        }}
                        sx={{
                          color: "#4A5565",
                          flexShrink: 0,
                          opacity: hoveredCard === rule.id ? 1 : 0.45,
                          transition: "opacity 0.2s",
                          "&:hover": {
                            bgcolor: "rgba(255, 133, 85, 0.12)",
                            color: "#FF8555",
                            opacity: 1,
                          },
                        }}
                      >
                        {favoriteKeySet.has(getAlertRuleFavoriteKey(rule)) ? (
                          <StarIcon sx={{ fontSize: 20, color: "#FF8555" }} />
                        ) : (
                          <StarBorderIcon sx={{ fontSize: 20, color: "#9E9E9E" }} />
                        )}
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      aria-label="Rule actions"
                      onClick={(e) => {
                        e.stopPropagation();
                        onMenuOpen(e, rule);
                      }}
                      sx={{
                        color: "#4A5565",
                        flexShrink: 0,
                        opacity: hoveredCard === rule.id ? 1 : 0.45,
                        transition: "opacity 0.2s",
                        "&:hover": {
                          bgcolor: "rgba(41, 50, 229, 0.08)",
                          color: "#2932E5",
                          opacity: 1,
                        },
                      }}
                    >
                      <MoreVertIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                </Box>

                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.25 }}>
                  <Chip
                    label={rule.isActive ? "Active" : "Inactive"}
                    size="small"
                    sx={{
                      height: 24,
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "11px",
                      fontWeight: 600,
                      backgroundColor: rule.isActive ? "#E8F5E9" : "#FFEBEE",
                      color: rule.isActive ? "#2E7D32" : "#C62828",
                    }}
                  />
                  <Chip
                    label={rule.alertType || "—"}
                    size="small"
                    sx={{
                      height: 24,
                      maxWidth: "100%",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "11px",
                      fontWeight: 600,
                      backgroundColor: alertTypeColor.bg,
                      color: alertTypeColor.text,
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                </Stack>

                <Divider sx={{ borderColor: "#e8eaef", my: 0.5 }} />

                <Stack spacing={1} sx={{ flex: 1, minHeight: 0, mt: 1 }}>
                  {rule.severityLabel && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#5f6368",
                          fontFamily: "Roboto, sans-serif",
                          flex: "0 0 72px",
                        }}
                      >
                        Severity
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#212121",
                          fontFamily: "Roboto, sans-serif",
                          minWidth: 0,
                        }}
                      >
                        {rule.severityLabel}
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#5f6368",
                        fontFamily: "Roboto, sans-serif",
                        flex: "0 0 72px",
                      }}
                    >
                      Schedule
                    </Typography>
                    <Chip
                      label={rule.schedule || "Forever"}
                      size="small"
                      sx={{
                        height: 24,
                        maxWidth: "100%",
                        fontFamily: "Roboto, sans-serif",
                        fontSize: "11px",
                        fontWeight: 600,
                        backgroundColor: "#FFF3E0",
                        color: "#E65100",
                        "& .MuiChip-label": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#5f6368",
                        fontFamily: "Roboto, sans-serif",
                        flex: "0 0 72px",
                        pt: 0.25,
                      }}
                    >
                      Source
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <VideocamIcon
                        sx={{ fontSize: 18, color: "#2932E5", flexShrink: 0 }}
                      />
                      <Tooltip
                        title={
                          rule.sourceLabel ??
                          rule.sourceCategory ??
                          "—"
                        }
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "#212121",
                            fontFamily: "Roboto, sans-serif",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {rule.sourceLabel ??
                            rule.sourceCategory ??
                            "—"}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#5f6368",
                        fontFamily: "Roboto, sans-serif",
                        flex: "0 0 72px",
                        pt: 0.25,
                      }}
                    >
                      Sub-source
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        minWidth: 0,
                        flex: 1,
                      }}
                    >
                      <VideocamIcon
                        sx={{ fontSize: 18, color: "#2932E5", flexShrink: 0 }}
                      />
                      <Tooltip
                        title={
                          rule.subSourceLabel ||
                          rule.sourceName ||
                          ""
                        }
                      >
                        {typeof onSubSourceClick === "function" ? (
                          <Button
                            type="button"
                            variant="text"
                            disabled={!ruleCanInteractSubSourceColumn(rule)}
                            onClick={(e) => onSubSourceClick(e, rule)}
                            sx={{
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "13px",
                              fontWeight: 500,
                              textTransform: "none",
                              p: 0,
                              minWidth: 0,
                              justifyContent: "flex-start",
                              color: "rgba(0, 0, 0, 0.87)",
                              textDecoration: "none",
                              overflow: "hidden",
                              "&:hover:not(.Mui-disabled)": {
                                color: "#2932E5",
                                textDecoration: "underline",
                                textDecorationColor: "rgba(41, 50, 229, 0.35)",
                                bgcolor: "rgba(41, 50, 229, 0.04)",
                              },
                              "&.Mui-disabled": {
                                color: "rgba(0, 0, 0, 0.38)",
                                textDecoration: "none",
                              },
                            }}
                          >
                            <Typography
                              component="span"
                              noWrap
                              sx={{
                                fontFamily: "Roboto, sans-serif",
                                fontSize: "13px",
                                fontWeight: 500,
                              }}
                            >
                              {rule.subSourceLabel ||
                                rule.sourceName ||
                                "—"}
                            </Typography>
                          </Button>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "#212121",
                              fontFamily: "Roboto, sans-serif",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {rule.subSourceLabel ||
                              rule.sourceName ||
                              "—"}
                          </Typography>
                        )}
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#5f6368",
                        fontFamily: "Roboto, sans-serif",
                        flex: "0 0 72px",
                        pt: 0.25,
                      }}
                    >
                      Notified
                    </Typography>
                    <Tooltip title={rule.notifiedTo || "—"}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#212121",
                          fontFamily: "Roboto, sans-serif",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          flex: 1,
                        }}
                      >
                        {rule.notifiedTo || "—"}
                      </Typography>
                    </Tooltip>
                  </Box>
                </Stack>

                <Divider sx={{ borderColor: "#e8eaef", my: 1.25 }} />

                <Box>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: "#5f6368",
                      fontFamily: "Roboto, sans-serif",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      mb: 0.75,
                    }}
                  >
                    Actions
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {actions.length === 0 ? (
                      <Typography
                        variant="caption"
                        sx={{ color: "#9e9e9e", fontFamily: "Roboto, sans-serif" }}
                      >
                        None
                      </Typography>
                    ) : (
                      <>
                        {actions.slice(0, 3).map((type, index) => (
                          <Chip
                            key={`${type}-${index}`}
                            label={type}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: "10px",
                              fontFamily: "Roboto, sans-serif",
                              ...actionChipSx(type),
                            }}
                          />
                        ))}
                        {actions.length > 3 && (
                          <Tooltip
                            title={
                              <Stack spacing={0.25}>
                                {actions.slice(3).map((type, i) => (
                                  <Typography
                                    key={i}
                                    variant="caption"
                                    sx={{ fontSize: "11px" }}
                                  >
                                    {type}
                                  </Typography>
                                ))}
                              </Stack>
                            }
                            arrow
                          >
                            <Chip
                              label={`+${actions.length - 3}`}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: "10px",
                                fontWeight: 600,
                                cursor: "help",
                                backgroundColor: "#EDE7F6",
                                color: "#5E35B1",
                                fontFamily: "Roboto, sans-serif",
                              }}
                            />
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Stack>
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    mt: "auto",
                    pt: 1.25,
                    display: "block",
                    color: "#9e9e9e",
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "11px",
                  }}
                >
                  Created {rule.createdDate || "—"}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
}
